/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import humanize from 'humanize-plus';
import { DateTime } from 'luxon';
import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryCache, useMutation } from 'react-query';
import { useDispatch } from 'react-redux';

import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { Button, CircularProgress, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import { AlignCenter } from '../../../../../../components/AlignCenter/AlignCenter';
import { HUSpin } from '../../../../../../components/HUSpin/HUSpin';
import { SimpleConfirmModal } from '../../../../../../components/SimpleConfirmModal/SimpleConfirmModal';
import { SimpleNotifyModal } from '../../../../../../components/SimpleNotifyModal/SimpleNotifyModal';
import { Namespaces } from '../../../../../../i18n';
import { ClusterApiService } from '../../../../../../services/ClusterApiService';
import { enqueueHUSnackbar } from '../../../../../../store/notifier/actions';
import { useTableStyles } from '../../../../../../styles/table';
import { ApiClientError } from '../../../../../../types/api/ApiClientError';
import { ApiClientResponseData } from '../../../../../../types/api/ApiClientResponse';
import { ClusterLogicalResponsePayload } from '../../../../../../types/cluster/ClusterLogicalResponsePayload';
import { UpdateClusterDetailsParameterIdentification } from '../../../../../../types/cluster/UpdateClusterDetailsParameterIdentification';
import { fetchClusterLogicalBackups } from '../../../../../../utils/queryFns';
import { reactToRemoveLogicalBackup } from '../../../../../../utils/reactions';
import { saveFile } from '../../../../../../utils/saveFile';
import { jwtToken } from '../../../../../../utils/storageHelpers';
import { createBackupsTableStyles } from './BackupsTable.styles';

interface BackupTableProps {
  identificationData: UpdateClusterDetailsParameterIdentification;
  logicalBackups: ClusterLogicalResponsePayload[];
  utcLocal: string;
  utcServer: string;
  createBackupButton: React.ReactElement<any>;
}

export const BackupsTable = memo<BackupTableProps>(
  ({ identificationData, logicalBackups, utcLocal, utcServer, createBackupButton }) => {
    const { t } = useTranslation(Namespaces.Cluster);
    const classes = createBackupsTableStyles();
    const tableClasses = useTableStyles();
    const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);
    const [isConfirmDeleteBackupModal, setIsConfirmDeleteBackupModal] = useState<boolean>(false);
    const [isConfirmDeleteAllBackupsModal, setIsConfirmDeleteAllBackupsModal] = useState<boolean>(false);
    const [currentBackupName, setCurrentBackupName] = useState<string>('');
    const [currentBackupFileName, setCurrentBackupFileName] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [downloadingBackups, setDownloadingBackups] = useState<Set<string>>(new Set());
    const dispatch = useDispatch();

    const sortedBackups = useMemo(
      () => logicalBackups.sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()),
      [logicalBackups],
    );

    const removeBackup = (params: { organizationId: string; projectId: string; clusterId: string; name: string }) =>
      ClusterApiService.deleteClusterBackup(params);

    const [deleteBackup] = useMutation(removeBackup, {
      onMutate: ({ name, ...identificationData }) => {
        queryCache.cancelQueries([fetchClusterLogicalBackups.key, identificationData]);

        const queryBackupData = queryCache.getQueryData<ApiClientResponseData<ClusterLogicalResponsePayload[]>>([
          fetchClusterLogicalBackups.key,
          identificationData,
        ]);

        const previousBackups = queryBackupData?.payload ?? [];

        const indexOf = previousBackups.findIndex(obj => obj.name === name);

        if (indexOf > -1) {
          const currentBackups = [...previousBackups];
          const backupToDelete = currentBackups.splice(indexOf, 1);
          queryCache.setQueryData([fetchClusterLogicalBackups.key, identificationData], {
            message: 'successful',
            payload: currentBackups,
          } as ApiClientResponseData);
          return () => {
            const nextBackups = [...currentBackups];
            nextBackups.splice(indexOf, 0, backupToDelete[0]);
            queryCache.setQueryData([fetchClusterLogicalBackups.key, identificationData], {
              message: 'successful',
              payload: nextBackups,
            } as ApiClientResponseData);
          };
        }
        return () => {};
      },
      onSettled: (_data, _error, variables) => {
        void reactToRemoveLogicalBackup(variables);
      },
      onError: (e, _variable, rollback) => {
        console.log('onError');
        const error = e as ApiClientError;
        console.log(error);
        setIsOpenErrorModal(true);
        (rollback as () => any)();
      },
    });

    const onDeleteClusterBackup = async (name: string) => {
      setIsConfirmDeleteBackupModal(false);
      try {
        await deleteBackup({ name, ...identificationData });
      } catch (e) {
        console.log(e);
      }
    };

    const onDeleteAllClusterBackups = async () => {
      let shouldOpenErrorModal = false;
      setIsConfirmDeleteAllBackupsModal(false);
      setIsLoading(true);
      const promises = sortedBackups.map(async backup => {
        try {
          await removeBackup({ name: backup.name, ...identificationData });
        } catch (e) {
          shouldOpenErrorModal = true;
          console.log(e);
        }
      });
      await Promise.all(promises);
      await reactToRemoveLogicalBackup({ ...identificationData });
      setIsLoading(false);
      shouldOpenErrorModal && setIsOpenErrorModal(true);
    };

    const handleBackupDownload = async (link: string, fileName: string): Promise<void> => {
      try {
        setDownloadingBackups(prev => new Set([...prev, fileName]));
        const headers = new Headers({ authorization: `Bearer ${jwtToken.get()}` });
        const response = await window.fetch(link, { headers });

        const blob = await response.blob();
        if (blob.size === 0) {
          throw new Error('Empty backup file');
        }

        await saveFile(blob, fileName);
      } catch (error) {
        console.error('Failed to download backup:', error);
        const errorMessage = error instanceof Error ? error.message : t('common:errors.unexpected_error');
        dispatch(enqueueHUSnackbar({ primaryText: errorMessage }, { variant: 'error' }));
      } finally {
        setDownloadingBackups(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileName);
          return newSet;
        });
      }
    };

    return (
      <HUSpin spinning={isLoading} delay={50}>
        <Grid classes={{ root: classes.root }}>
          <Typography variant={'h3'} classes={{ root: classes.heading }}>
            {t('details.backups.backups_list')}
          </Typography>
          <Table size="small" data-cy="backups-table">
            <TableHead>
              <TableRow>
                <TableCell variant="head" classes={{ root: classes.tableHeadCell }}>
                  <Typography classes={{ root: classes.cellHead }}>{t('details.backups.table.name')}</Typography>
                </TableCell>
                <TableCell variant="head" classes={{ root: classes.tableHeadCell }}>
                  <Typography classes={{ root: classes.cellHead }}>{t('details.backups.table.date')}</Typography>
                </TableCell>
                <TableCell variant="head" classes={{ root: classes.tableHeadCell }}>
                  <Typography classes={{ root: classes.cellHead }}>{t('details.backups.table.size')}</Typography>
                </TableCell>
                <TableCell colSpan={2} variant="head" classes={{ root: classes.tableHeadCell }}>
                  {createBackupButton}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedBackups.length ? (
                sortedBackups.map(row => {
                  const fileName = row.name.split('/').reverse()[0];
                  const size = humanize.fileSize(row.sizeInBytes);
                  const createdAtLocal = DateTime.fromISO(row.lastModified).toFormat(
                    `dd LLLL, yyyy '/' HH:mm '(${utcLocal})'`,
                  );
                  const createdAtServer = DateTime.fromISO(row.lastModified)
                    .toUTC()
                    .toFormat(`dd LLLL, yyyy '/' HH:mm '(${utcServer})'`);
                  const downloadLink = ClusterApiService.getClusterBackupEndpoint({
                    ...identificationData,
                    name: row.name,
                  });
                  const isDownloading = downloadingBackups.has(fileName);

                  return (
                    <TableRow key={row.name}>
                      <TableCell classes={{ root: classes.tableCell }}>
                        <Typography className={tableClasses.bodyTableCellText}>{fileName}</Typography>
                      </TableCell>
                      <TableCell classes={{ root: classes.tableCell }}>
                        <Typography className={tableClasses.bodyTableCellText} classes={{ root: classes.dateTop }}>
                          {createdAtServer}
                        </Typography>
                        <Typography className={tableClasses.bodyTableCellText}>{createdAtLocal}</Typography>
                      </TableCell>
                      <TableCell classes={{ root: classes.tableCell }}>
                        <Typography className={tableClasses.bodyTableCellText}>{size}</Typography>
                      </TableCell>
                      <TableCell align="right" classes={{ root: classes.tableButtonCell }}>
                        <Tooltip title={t('details.backups.table.actions.download')}>
                          <IconButton
                            size="large"
                            onClick={() => handleBackupDownload(downloadLink, fileName)}
                            disabled={isDownloading}
                            data-cy={`backup-download-button-${fileName}`}
                          >
                            {isDownloading ? <CircularProgress size={24} /> : <DownloadIcon />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell classes={{ root: classes.tableButtonCell }}>
                        <Tooltip title={t('details.header.delete')}>
                          <IconButton
                            size="large"
                            onClick={() => {
                              setCurrentBackupName(row.name);
                              setCurrentBackupFileName(fileName);
                              setIsConfirmDeleteBackupModal(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={5} classes={{ root: classes.tableCell }}>
                    <AlignCenter>
                      <Typography className={tableClasses.bodyTableCellText}>
                        {t('details.backups.no_backups')}
                      </Typography>
                    </AlignCenter>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {!!sortedBackups.length && (
            <Grid sx={{ display: 'flex', justifyContent: 'right', pr: '11px' }}>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setIsConfirmDeleteAllBackupsModal(true)}
                classes={{ root: classes.deleteAllBackupsButton }}
              >
                {t('details.backups.delete_all_backups')}
              </Button>
            </Grid>
          )}
          <SimpleConfirmModal
            open={isConfirmDeleteBackupModal}
            message={t('details.backups.want_delete_backup', {
              name: currentBackupFileName,
            })}
            onConfirm={() => onDeleteClusterBackup(currentBackupName)}
            onCancel={() => setIsConfirmDeleteBackupModal(false)}
          />
          <SimpleConfirmModal
            open={isConfirmDeleteAllBackupsModal}
            message={t('details.backups.want_delete_all_backups')}
            onConfirm={onDeleteAllClusterBackups}
            onCancel={() => setIsConfirmDeleteAllBackupsModal(false)}
          />
          <SimpleNotifyModal
            open={isOpenErrorModal}
            message={t(`${Namespaces.Common}:errors.unexpected_error`)}
            onOk={() => setIsOpenErrorModal(false)}
          />
        </Grid>
      </HUSpin>
    );
  },
);
